import React from "react";
import { pdf, Document, Page, View, Text, Link, StyleSheet, Svg, G, Path } from "@react-pdf/renderer";
import { trackWebshopClickEvent } from "../packages/google-analytics/googleAnalyticsEventHelper";
import SummaryTablePdf, {
    SummaryTablePdfProps,
} from "../packages/fwa/components/organisms/SummaryTable/SummaryTablePdf";
import type { TeliaPlaySubscription } from "../../models/state/teliaPlay";
import * as properNameCase from "../../shared/properNameCase";
import * as color from "@telia/teddy/tokens/color";

const TELIA_PURPLE = color.teddyColorBrandCorePurple;
const FOOTER_TEXT = "Telia Norge AS, Organisasjonsnummer 981 929 055, Oslo";

const TeliaLogo: React.FC<{ width?: number }> = ({ width = 120 }) => (
    <Svg viewBox="0 0 760.71 300" style={{ width, height: (300 / 760.71) * width, marginBottom: 16 }}>
        <G>
            <G>
                <Path fill={TELIA_PURPLE} d="M455.19,105.91H408.9v130.4h-29.37v-130.4H332.8V80.54h122.39V105.91z" />
                <Path
                    fill={TELIA_PURPLE}
                    d="M543.31,188.47H461.2c2.89,16.47,14.69,27.37,29.82,27.37c11.35,0,20.47-5.12,26.04-13.57l21.81,13.13 c-11.13,14.91-29.37,23.81-47.84,23.81c-30.48,0-56.96-24.25-56.96-58.97s26.26-58.74,55.63-58.74 C528.18,121.49,547.09,152.2,543.31,188.47z M462.09,167.77h54.74c-3.34-14.46-12.68-23.59-27.15-23.59 C476.11,144.19,465.87,153.53,462.09,167.77z"
                />
                <Path
                    fill={TELIA_PURPLE}
                    d="M601.61,236.75c-4.23,0.89-8.68,1.11-12.68,1.11c-19.58,0-28.26-9.35-28.26-28.26V80.54h27.37v125.28 c0,9.57,5.79,10.9,13.57,10.01V236.75z"
                />
                <Path
                    fill={TELIA_PURPLE}
                    d="M615.85,93.45c0-8.68,7.57-15.8,16.24-15.8c8.46,0,16.02,7.12,16.02,15.8c0,8.68-7.57,15.8-16.02,15.8 C623.42,109.25,615.85,102.13,615.85,93.45z M618.3,236.31V125.05h27.37v111.26H618.3z"
                />
                <Path
                    fill={TELIA_PURPLE}
                    d="M760.71,157.54v78.77h-25.59V223.4c-8.68,11.35-22.48,15.58-34.05,15.58c-24.03,0-38.27-15.13-38.27-34.05 c0-21.58,24.26-39.16,53.85-39.16c5.79,0,12.02,0.45,17.58,2.23v-7.34c0-11.13-6.45-16.91-19.58-16.91 c-11.79,0-22.25,6.01-28.7,13.8l-19.36-14.24c10.9-12.68,29.6-21.58,48.51-21.58C744.69,121.71,760.71,134.62,760.71,157.54z M734.23,193.36v-4.9c-4.45-1.56-9.12-2-14.91-2c-20.03,0-30.26,8.01-30.26,17.8c0,8.01,6.9,13.13,17.36,13.13 C720.21,217.39,734.23,209.16,734.23,193.36z"
                />
            </G>
            <G>
                <Path
                    fill={TELIA_PURPLE}
                    d="M196.36,210.79c38.9-22.56,73.94-60.82,84.99-88.1c3.14-22.58,3.78-25.43,4.16-50.27 c0.05-3.44-0.16-6.74-0.48-9.9c-2.27,36.48-49.42,82.35-111.8,112.32c-75.25,36.15-131.7,45.88-156.89,18.31 c8.75,17.28,14.49,25.67,24.33,38.49C69.73,257.38,130.89,248.77,196.36,210.79z"
                />
                <Path
                    fill={TELIA_PURPLE}
                    d="M164.46,148.99c70.98-34.24,120.42-82.95,111.8-115.24C262.11,9.31,233.47,0,202.76,0C111.73,0,0,60,0,136.55 c0,9.84,2.17,20.84,6.06,32.37C26.82,196.47,90.3,184.76,164.46,148.99z"
                />
                <Path
                    fill={TELIA_PURPLE}
                    d="M158.47,257.49L158.47,257.49c-49.92,20.24-78.49,13.79-94.28,1.17l-0.23-0.18 c24.44,24.38,52.3,41.53,76.73,41.53c42.78,0,101.35-51.9,129.07-132.38C254.14,196.68,220.62,232.37,158.47,257.49z"
                />
            </G>
        </G>
    </Svg>
);

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: "Helvetica",
        fontSize: 12,
        color: "#000000",
    },
    heading: {
        fontSize: 20,
        marginBottom: 10,
    },
    subheading: {
        fontSize: 14,
        marginBottom: 10,
    },
    body: {
        fontSize: 12,
        marginBottom: 10,
        lineHeight: 1.4,
    },
    link: {
        color: TELIA_PURPLE,
        marginBottom: 10,
    },
    cartImage: {
        marginTop: 15,
        objectFit: "contain",
    },
    footer: {
        position: "absolute",
        bottom: 20,
        left: 30,
        fontSize: 8,
        fontFamily: "Helvetica-Oblique",
        color: "#555555",
    },
    disclaimerBlock: {
        marginTop: 20,
    },
    disclaimerText: {
        fontSize: 10,
        marginBottom: 6,
        lineHeight: 1.4,
    },
    disclaimerLinkLabel: {
        fontSize: 10,
        marginBottom: 4,
    },
    disclaimerLink: {
        color: TELIA_PURPLE,
        fontSize: 10,
        marginBottom: 4,
    },
});

interface PdfDocumentProps {
    summaryData?: SummaryTablePdfProps;
}

const PdfDocument: React.FC<PdfDocumentProps> = ({ summaryData }) => (
    <Document>
        <Page size="A3" orientation="portrait" style={styles.page}>
            <TeliaLogo />
            <View>
                <Text style={styles.heading}>Våre standardvilkår</Text>
                <Text style={styles.subheading}>
                    Avtalen forutsetter en bindingstid på 12 måneder, og du har 14 dagers angrerett etter akseptert
                    tilbud i henhold til angrerettloven.
                </Text>
                <Link style={styles.link} src="https://www.telia.no/hjelp/vilkar-og-angrerett/">
                    Les alle våre standardvilkår her
                </Link>
                <Text style={styles.body}>
                    Her finner du også angrerettskjema. Bruddgebyret er lik totalrabatten du får i kampanjen. Gebyret
                    reduseres med en tolvtedel hver måned, ned til minimum 399 kr. Telia tar forbehold om at det i
                    tilbudet for øvrig ikke er økonomiske eller tekniske hindringer for leveranse av tjenesten(e)
                </Text>
            </View>
            {summaryData ? <SummaryTablePdf {...summaryData} /> : null}
            <Text style={styles.footer}>{FOOTER_TEXT}</Text>
        </Page>
    </Document>
);

const downloadPdf = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${fileName}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export type TeliaPlaySummaryCustomer = {
    name: string;
    address: string;
    city: string;
};

// react-pdf cannot read CSS variables, so Teddy color tokens are used as values.
// Teddy only exports color tokens as JS, so sizes mirror the Teddy spacing (100–400) and border-radius (md/full) scales.
const SPACE_XS = 8;
const SPACE_SM = 16;
const SPACE_MD = 24;
const SPACE_LG = 32;
const RADIUS = 8;
const RADIUS_FULL = 999;
const BORDER_WEAK = `1px solid ${color.teddyColorBorderWeak}`;

const teliaPlayStyles = StyleSheet.create({
    page: {
        paddingTop: SPACE_LG,
        paddingHorizontal: SPACE_LG,
        paddingBottom: SPACE_LG * 2,
        fontFamily: "Helvetica",
        fontSize: 11,
        color: color.teddyColorTextDefault,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
    },
    muted: {
        fontSize: 9,
        color: color.teddyColorTextMedium,
        textTransform: "uppercase",
        letterSpacing: 1,
    },
    accentBar: {
        height: 4,
        backgroundColor: color.teddyColorBrandCorePurple,
        borderRadius: 2,
        marginBottom: SPACE_MD,
    },
    title: {
        fontSize: 26,
        fontFamily: "Helvetica-Bold",
        marginBottom: SPACE_XS,
    },
    intro: {
        color: color.teddyColorTextMedium,
        marginBottom: SPACE_MD,
    },
    cards: {
        flexDirection: "row",
        gap: SPACE_SM,
        marginBottom: SPACE_MD,
    },
    card: {
        flex: 1,
        backgroundColor: color.teddyColorBackgroundSecondary,
        borderRadius: RADIUS,
        padding: SPACE_SM,
    },
    cardLabel: {
        marginBottom: SPACE_XS,
    },
    bold: {
        fontSize: 13,
        fontFamily: "Helvetica-Bold",
        marginBottom: SPACE_XS,
    },
    text: {
        fontSize: 11,
        lineHeight: 1.5,
    },
    productName: {
        fontSize: 15,
        fontFamily: "Helvetica-Bold",
        color: color.teddyColorBrandCorePurple,
        marginBottom: SPACE_XS,
    },
    badge: {
        alignSelf: "flex-start",
        backgroundColor: color.teddyColorBrandLightPurple,
        color: color.teddyColorBrandCorePurple,
        fontSize: 9,
        fontFamily: "Helvetica-Bold",
        borderRadius: RADIUS_FULL,
        paddingVertical: 3,
        paddingHorizontal: SPACE_XS,
        marginBottom: SPACE_SM,
    },
    priceRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        borderTop: BORDER_WEAK,
        paddingTop: SPACE_XS,
    },
    totalBox: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: color.teddyColorBrandCorePurple,
        color: color.teddyColorFunctionalWhite,
        fontFamily: "Helvetica-Bold",
        borderRadius: RADIUS,
        padding: SPACE_SM,
    },
    totalValue: {
        fontSize: 18,
    },
    footer: {
        position: "absolute",
        bottom: SPACE_MD,
        left: SPACE_LG,
        right: SPACE_LG,
        borderTop: BORDER_WEAK,
        paddingTop: SPACE_XS,
        fontSize: 8,
        color: color.teddyColorTextMedium,
    },
});

const formatPdfDate = (date: Date): string =>
    [date.getDate(), date.getMonth() + 1].map((part) => String(part).padStart(2, "0")).join(".") +
    `.${date.getFullYear()}`;

export const saveTeliaPlaySummaryToPdf = async (
    subscription: TeliaPlaySubscription,
    customer: TeliaPlaySummaryCustomer,
): Promise<void> => {
    const monthlyPrice = `${Number(subscription.monthlyPrice.regularPrice)},-/md.`;
    const blob = await pdf(
        <Document title="Telia Play – ordreoppsummering" author="Telia Norge AS">
            <Page size="A4" style={teliaPlayStyles.page}>
                <View style={teliaPlayStyles.header}>
                    <TeliaLogo width={110} />
                    <View>
                        <Text style={teliaPlayStyles.muted}>Dato</Text>
                        <Text>{formatPdfDate(new Date())}</Text>
                    </View>
                </View>
                <View style={teliaPlayStyles.accentBar} />
                <Text style={teliaPlayStyles.title}>Ordreoppsummering</Text>
                <Text style={teliaPlayStyles.intro}>Takk for at du velger Telia Play.</Text>
                <View style={teliaPlayStyles.cards}>
                    <View style={teliaPlayStyles.card}>
                        <Text style={[teliaPlayStyles.muted, teliaPlayStyles.cardLabel]}>Kunde</Text>
                        <Text style={teliaPlayStyles.bold}>{properNameCase.convert(customer.name)}</Text>
                        <Text style={teliaPlayStyles.text}>{properNameCase.convert(customer.address)}</Text>
                        <Text style={teliaPlayStyles.text}>{customer.city}</Text>
                    </View>
                    <View style={teliaPlayStyles.card}>
                        <Text style={[teliaPlayStyles.muted, teliaPlayStyles.cardLabel]}>Ditt abonnement</Text>
                        <Text style={teliaPlayStyles.productName}>{subscription.subscriptionName}</Text>
                        {subscription.subscriptionShortname ? (
                            <Text style={teliaPlayStyles.badge}>{subscription.subscriptionShortname}</Text>
                        ) : null}
                        <View style={teliaPlayStyles.priceRow}>
                            <Text style={teliaPlayStyles.text}>Per måned</Text>
                            <Text style={teliaPlayStyles.text}>{monthlyPrice}</Text>
                        </View>
                    </View>
                </View>
                <View style={teliaPlayStyles.totalBox}>
                    <Text>Totalt per måned</Text>
                    <Text style={teliaPlayStyles.totalValue}>{monthlyPrice}</Text>
                </View>
                <Text style={teliaPlayStyles.footer} fixed>
                    {FOOTER_TEXT}
                </Text>
            </Page>
        </Document>,
    ).toBlob();
    downloadPdf(blob, "telia-play-oppsummering");
};

export const saveToPdf = async (fileName: string, summaryData?: SummaryTablePdfProps) => {
    trackWebshopClickEvent({
        action: "click",
        text: "Last ned ordreoppsummering",
        type: "link",
        url: "/adressesok/personlige-opplysninger/",
        context: "link_fwa_checkout",
    });

    if (!summaryData) {
        throw new Error("Could not find any added items");
    }

    const blob = await pdf(<PdfDocument summaryData={summaryData} />).toBlob();

    downloadPdf(blob, fileName);
};
